import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { isValidSshGitUrl } from "@utils/common";
import CustomSelect from "@components/UI/CustomSelect";

const REPOSITORY_TYPES = [
    { value: "PTA", label: "PTA" },
    { value: "SSH", label: "SSH" },
];

const CloneRepository = ({
    control,
    colors,
    t,
    watchImportMethod,
    watchRepositoryType,
    watchSshUrl,
    errors,
    urlRules,
    accessTokenRules,
    sshUrlRules,
}) => {
    const resolvedUrlRules =
        urlRules ||
        {
            required: t("project.verification-request.url-required"),
            pattern: {
                value: /^https?:\/\/.+/,
                message: t("project.verification-request.url-invalid"),
            },
        };

    const resolvedAccessTokenRules =
        accessTokenRules ||
        {
            required: t("project.verification-request.token-required"),
            minLength: {
                value: 10,
                message: t("project.verification-request.token-min-length"),
            },
        };

    const resolvedSshRules =
        sshUrlRules ||
        {
            required:
                watchImportMethod === "repository" && watchRepositoryType === "SSH" && !isValidSshGitUrl(watchSshUrl)
                    ? t("project.verification-request.ssh-url-required")
                    : false,
            pattern: {
                value: /^git@[^@\s]+:[^@\s]+\/[^@\s]+(\.git)?$/i,
                message: t("project.verification-request.ssh-url-invalid"),
            },
        };

    return (
        <div
            data-testid="clone-repository"
            className={`mb-3 rounded-lg border p-4 transition-all duration-200 ${watchImportMethod === "repository"
                ? `${colors.borderSelectedOption} ${colors.bgSelectedOption}`
                : `${colors.borderPrimary}`
                }`}
        >
            <Controller
                name="importMethod"
                control={control}
                render={({ field }) => (
                    <div>
                        <label className="flex cursor-pointer items-center">
                            <input
                                type="radio"
                                {...field}
                                value="repository"
                                checked={field.value === "repository"}
                                className="sr-only"
                            />
                            <div
                                className={`mr-3 flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors ${field.value === "repository"
                                    ? `${colors.borderSelectedOption} ${colors.bgSelectedRadio}`
                                    : `${colors.borderPrimary} ${colors.bgPrimary}`
                                    }`}
                                aria-hidden="true"
                            >
                                {field.value === "repository" && <div className={`h-2 w-2 rounded-full ${colors.toggleThumbBackground}`}></div>}
                            </div>
                            <div className="flex items-center space-x-3">
                                <span className={`${colors.textPrimary} text-sm font-medium`}>{t("project.verification-request.repository")}</span>
                                {field.value === "repository" && (
                                    <Controller
                                        name="repositoryType"
                                        control={control}
                                        render={({ field: repoField }) => (
                                            <div
                                                className="w-40"
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <CustomSelect
                                                    options={REPOSITORY_TYPES}
                                                    value={REPOSITORY_TYPES.find((type) => type.value === repoField.value) || null}
                                                    onChange={(option) => repoField.onChange(option?.value)}
                                                    isSearchable={false}
                                                    clearable={false}
                                                    usePortal
                                                    size="sm"
                                                    className="w-full"
                                                    data-testid="clone-repository-type-select"
                                                    aria-label="Repository type"
                                                />
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        </label>

                        {field.value === "repository" && (
                            <div className="mt-3 space-y-3">
                                {watchRepositoryType === "PTA" ? (
                                    <div className="space-y-3">
                                        <Controller
                                            name="url"
                                            control={control}
                                            rules={resolvedUrlRules}
                                            render={({ field: urlField }) => (
                                                <div>
                                                    <input
                                                        {...urlField}
                                                        type="url"
                                                        placeholder={t("project.verification-request.repository-url")}
                                                        className={`w-full rounded-lg px-3 py-2 text-sm ${colors.textPlaceholder} transition-colors ${colors.focusBorder} focus:ring-2 ${colors.focusRingPrimarySoft} focus:outline-none ${colors.bgPrimary} ${colors.textPrimary} ${colors.borderPrimary} ${errors.url ? colors.borderDangerOutLineButton : ""}`}
                                                        aria-describedby={errors.url ? "url-error" : undefined}
                                                        autoComplete="off"
                                                        data-lpignore="true"
                                                        data-form-type="other"
                                                    />
                                                    {errors.url && (
                                                        <p id="url-error" className={`mt-1 text-xs ${colors.textDanger}`}>
                                                            {errors.url.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                        <Controller
                                            name="accessToken"
                                            control={control}
                                            rules={resolvedAccessTokenRules}
                                            render={({ field: tokenField }) => (
                                                <div>
                                                    <input
                                                        {...tokenField}
                                                        type="password"
                                                        placeholder={t("project.verification-request.access-token")}
                                                        className={`w-full rounded-lg px-3 py-2 text-sm ${colors.textPlaceholder} transition-colors ${colors.focusBorder} focus:ring-2 ${colors.focusRingPrimarySoft} focus:outline-none ${colors.bgPrimary} ${colors.textPrimary} ${colors.borderPrimary} ${errors.accessToken ? colors.borderDangerOutLineButton : ""}`}
                                                        aria-describedby={errors.accessToken ? "token-error" : undefined}
                                                        autoComplete="one-time-code"
                                                        data-lpignore="true"
                                                        data-form-type="other"
                                                    />
                                                    {errors.accessToken && (
                                                        <p id="token-error" className={`mt-1 text-xs ${colors.textDanger}`}>
                                                            {errors.accessToken.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                ) : (
                                    <Controller
                                        name="sshUrl"
                                        control={control}
                                        rules={resolvedSshRules}
                                        render={({ field: sshField }) => (
                                            <div>
                                                <input
                                                    {...sshField}
                                                    type="text"
                                                    placeholder={t("project.verification-request.ssh-url")}
                                                    className={`w-full rounded-lg px-3 py-2 text-sm ${colors.textPlaceholder} transition-colors ${colors.focusBorder} focus:ring-2 ${colors.focusRingPrimarySoft} focus:outline-none ${colors.bgPrimary} ${colors.textPrimary} ${colors.borderPrimary} ${errors.sshUrl ? colors.borderDangerOutLineButton : ""}`}
                                                    autoComplete="off"
                                                />
                                                {errors.sshUrl && (
                                                    <p className={`mt-1 text-xs ${colors.textDanger}`}>{errors.sshUrl.message}</p>
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            />
        </div>
    );
};

CloneRepository.propTypes = {
    control: PropTypes.object.isRequired,
    colors: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    watchImportMethod: PropTypes.string,
    watchRepositoryType: PropTypes.string,
    watchSshUrl: PropTypes.string,
    errors: PropTypes.object,
    urlRules: PropTypes.object,
    accessTokenRules: PropTypes.object,
    sshUrlRules: PropTypes.object,
};

export default CloneRepository;
